import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

import { searchToObject } from 'ouisys-engine/utilities/searchToObject';
import { tracker } from 'ouisys-engine/utilities/tracker';
import { setConfig } from 'ouisys-engine/utilities/getConfig';
import configJson from '../../config.json';
import { IStrategy } from 'ouisys-engine/strategy';
import { serviceLinks, portfolioUrl } from '../utils/configs';
const config = configJson as IStrategy;
setConfig(configJson as IStrategy);
const RootContext = createContext(undefined);

// custom hook to easily use the context accross the app
export const useRootContext = () => useContext(RootContext);

export const RootProvider = ({ children }) => {
  const [locale, setLocale] = useState('en');
  const urlParams = searchToObject(window);
  const d_locale = urlParams['locale'];
  const [isApplePayAvailable, setIsApplePayAvailable] = useState(false);
  const [isGooglePayAvailable, setIsGooglePayAvailable] = useState(false);
  const [userCanMakeGooglePayment, setUserCanMakeGooglePayment] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const serviceName = config?.strategyConfigs?.default?.flowConfig?.service || 'movio';

  const switchLang = (lang: string) => {
    setLocale(lang);
    if (typeof window !== 'undefined') {
      window.languageCode = lang;
      document.getElementsByTagName('html')[0].setAttribute('lang', lang);
    }

    //Set locale based on url designer para
    if (d_locale) {
      switchLang(d_locale);
    }
  };

  // We're converting locale here because portfolio page currently only has en, es and it.
  // This needs to be updated when we add more locale in the portfolio page.
  const localeOnPorfolioPage = ['en', 'es', 'it'];

  const getLink = (key) => {
    const adjustedLocale = localeOnPorfolioPage.includes(locale) ? locale : 'en';
    return serviceLinks[key].replace('{locale}', adjustedLocale);
  };

  const localePortfolioUrl = `${portfolioUrl[serviceName]}${localeOnPorfolioPage.includes(locale) ? `/${locale}/` : '/en/'}`;

  function isSafari() {
    const ua = navigator.userAgent;
    const vendor = navigator.vendor;

    const isSafariBrowser = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    return (
      vendor &&
      vendor.includes('Apple') && // Vendor is Apple
      ua &&
      ua.includes('Safari') && // User agent contains "Safari"
      !ua.includes('Chrome') && // Exclude Chrome
      !ua.includes('CriOS') && // Exclude Chrome on iOS
      !ua.includes('FxiOS') && // Exclude Firefox on iOS
      isSafariBrowser
    );
  }

  const langDetection = () => {
    if (typeof window != 'undefined') {
      // If browser language is not in array below, default to en
      if (!['en', 'fr', 'it', 'es', 'de', 'da'].includes(window.navigator.language)) {
        setLocale('en');
      }

      if (/^es\b/.test(window.navigator.language)) {
        setLocale('es');
        tracker.customEvent('Lang-Switcher', 'autodetect', 'browserLocale', 'es');
      }

      if (/^fr\b/.test(window.navigator.language)) {
        setLocale('fr');
        tracker.customEvent('Lang-Switcher', 'autodetect', 'browserLocale', 'fr');
      }

      if (/^it\b/.test(window.navigator.language)) {
        setLocale('it');
        tracker.customEvent('Lang-Switcher', 'autodetect', 'browserLocale', 'it');
      }

      if (/^de\b/.test(window.navigator.language)) {
        setLocale('de');
        tracker.customEvent('Lang-Switcher', 'autodetect', 'browserLocale', 'de');
      }

      if (/^da\b/.test(window.navigator.language)) {
        setLocale('da');
        tracker.customEvent('Lang-Switcher', 'autodetect', 'browserLocale', 'da');
      }

      if (/^en\b/.test(window.navigator.language)) {
        setLocale('en');
        tracker.customEvent('Lang-Switcher', 'autodetect', 'browserLocale', 'en');
      }
    }
  };

  const value = {
    locale,
    urlParams,
    switchLang,
    config,
    isApplePayAvailable,
    setIsApplePayAvailable,
    isGooglePayAvailable,
    setIsGooglePayAvailable,
    userCanMakeGooglePayment,
    setUserCanMakeGooglePayment,
    errorMessage,
    setErrorMessage,
    serviceName,
    getLink,
    localePortfolioUrl,
    isLoading,
    setIsLoading,
    isSafari
  };

  useEffect(() => {
    langDetection();
    if (typeof window != 'undefined') {
      window.languageCode = locale;
      document.getElementsByTagName('html')[0].setAttribute('lang', locale);
    }
    //Set locale based on url designer param
    if (d_locale) {
      setLocale(d_locale);
    }
  }, []);

  return <RootContext.Provider value={value}>{children}</RootContext.Provider>;
};
